/**
 * SortableChecklistItem Component
 * Extracted from ChecklistComponent.tsx
 * Individual draggable, editable checklist item with inline editing
 */

import React, { useState, useRef, useEffect } from 'react';
import { Check, X, GripVertical } from 'lucide-react';
import { ChecklistItem } from '@/shared/types/projectData';
import { useSettings } from '@/features/settings/contexts/SettingsContext';
import { cn, useTheme } from '@/ui/theme/ThemeContext';
import { useModalEscape } from '@/shared/services/KeyboardEventManager';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

export interface SortableChecklistItemProps {
  item: ChecklistItem;
  onToggle: (id: string) => void;
  onUpdate: (id: string, text: string) => void;
  onDelete: (id: string) => void;
  isEditing: boolean;
  onStartEdit: () => void;
  onFinishEdit: () => void;
}

export const SortableChecklistItem: React.FC<SortableChecklistItemProps> = ({
  item,
  onToggle,
  onUpdate,
  onDelete,
  isEditing,
  onStartEdit,
  onFinishEdit
}) => {
  const { theme } = useTheme();
  const { t } = useSettings();
  const [editText, setEditText] = useState(item.text);
  const inputRef = useRef<HTMLInputElement>(null);
  const hasEdited = useRef(false);
  const editFinished = useRef(false);

  useEffect(() => {
    if (!isEditing || !hasEdited.current) {
      setEditText(item.text);
    }
    if (!isEditing) hasEdited.current = false;
  }, [item.text, isEditing]);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: item.id,
    disabled: isEditing
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  useEffect(() => {
    editFinished.current = !isEditing;
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleSave = () => {
    if (!isEditing || editFinished.current) return;
    editFinished.current = true;
    const text = editText.trim();
    if (hasEdited.current && text && text !== item.text) {
      onUpdate(item.id, text);
    }
    onFinishEdit();
  };

  const handleCancel = () => {
    if (!isEditing || editFinished.current) return;
    editFinished.current = true;
    hasEdited.current = false;
    setEditText(item.text);
    onFinishEdit();
  };

  // Claim Escape above the hosting panel while editing so the first press
  // cancels only this draft (the manager stops Escape before handleKeyDown).
  useModalEscape('checklist-item-editor', handleCancel, {
    priority: 400,
    enabled: isEditing,
  });

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.nativeEvent.isComposing || e.nativeEvent.keyCode === 229) return;
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'group flex items-center gap-2 p-2 rounded-lg transition-all duration-200',
        theme.bgSecondary,
        isDragging && 'opacity-50 shadow-lg scale-105',
        item.checked && 'opacity-75'
      )}
    >
      {/* Drag Handle */}
      <div
        {...attributes}
        {...listeners}
        className={cn(
          'cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded',
          theme.hover,
          isEditing && 'opacity-0 pointer-events-none'
        )}
      >
        <GripVertical className="w-3 h-3 text-slate-400 dark:text-gray-400" />
      </div>

      {/* Checkbox */}
      <button
        onPointerDown={(e) => {
          e.stopPropagation();
        }}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onToggle(item.id);
        }}
        className={cn(
          'flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-all cursor-pointer',
          item.checked
            ? 'bg-green-500 border-green-500 text-white hover:bg-green-600'
            : cn('border-slate-300 dark:border-gray-600 hover:border-green-500', theme.hover)
        )}
        aria-label={`${t(item.checked ? 'Mark as incomplete' : 'Mark as complete')}: ${item.text}`}
        role="checkbox"
        aria-checked={item.checked}
        title={t(item.checked ? 'Mark as incomplete' : 'Mark as complete')}
      >
        {item.checked && <Check className="w-3 h-3" />}
      </button>

      {/* Text Content */}
      <div className="flex-1 min-w-0">
        {isEditing ? (
          <input
            ref={inputRef}
            type="text"
            value={editText}
            onPointerDown={(e) => {
              e.stopPropagation();
            }}
            onChange={(e) => {
              hasEdited.current = true;
              setEditText(e.target.value);
            }}
            onKeyDown={handleKeyDown}
            onBlur={handleSave}
            className={cn(
              'w-full px-2 py-1 text-sm bg-transparent border rounded',
              theme.borderPrimary,
              theme.textPrimary,
              theme.focusRing
            )}
          />
        ) : (
          <span
            onClick={onStartEdit}
            className={cn(
              'block text-sm cursor-text px-2 py-1 rounded transition-colors',
              theme.textPrimary,
              theme.hover,
              item.checked && 'line-through text-slate-500 dark:text-gray-400'
            )}
          >
            {item.text}
          </span>
        )}
      </div>

      {/* Delete Button */}
      <button
        onPointerDown={(e) => {
          e.stopPropagation();
        }}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onDelete(item.id);
        }}
        className={cn(
          'flex-shrink-0 p-1 rounded opacity-0 group-hover:opacity-100 transition-all',
          'text-red-500 hover:bg-red-500/10 focus:opacity-100'
        )}
        title={t('Delete checklist item')}
      >
        <X className="w-3 h-3" />
      </button>
    </div>
  );
};
